import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  MenuItem,
  Typography,
  Box,
  FormControlLabel,
} from "@mui/material";
import { Formik, Form } from "formik";
import { useSnackbar } from "notistack";
import { useState, ReactElement } from "react";
import * as Yup from "yup";

import { importOwnedPhoneNumberInputSchema } from "@stw/yup-validators";

import {
  ImportOwnedPhoneNumberMutation,
  ImportOwnedPhoneNumberMutationVariables,
  PhoneNumberMessageClass,
  ProviderName,
} from "~frontend/__generated__/graphql";
import { Checkbox } from "~frontend/components/Checkbox";
import { MessageClassDisplayName } from "~frontend/components/MessageClassDisplayName";
import { FormikSelect } from "~frontend/components/forms/FormSelect";
import { FormikTextField } from "~frontend/components/forms/FormTextField";
import FormikErrorBanner, {
  submitErrorField,
} from "~frontend/components/forms/FormikErrorBanner";
import { FormikSubmitButton } from "~frontend/components/forms/SubmitButton";
import { IMPORT_OWNED_PHONE_NUMBER } from "~frontend/graphql/ownedPhoneNumber";
import { GET_SUBACCOUNT_ADMIN_FIELDS } from "~frontend/graphql/subaccounts";
import { useCurrentUserIsStaff } from "~frontend/providers/LoggedInUserProvider";
import { useNonCacheClearingApolloClient } from "~frontend/providers/NonCacheClearingApolloClientProvider";
import { useErrorMessageFormatter } from "~frontend/util/errors";
import useMutation from "~frontend/util/useMutation";

import { PHONE_NUMBER_REFETCH_QUERIES } from ".";
import PhoneNumberConfig, {
  DEFAULT_SHORT_CODE_CONFIG_FORM_VALUES,
  shortCodeFormValuesToConfig,
} from "./PhoneNumberConfig";

export function ImportOwnedPhoneNumberDialog({
  open,
  onClose,
  subaccountId,
  hasActiveTFNumber = false,
}: {
  open: boolean;
  onClose: () => void;
  subaccountId: string;
  hasActiveTFNumber?: boolean;
}): ReactElement {
  const [importOwnedPhoneNumber] = useMutation<
    ImportOwnedPhoneNumberMutation,
    ImportOwnedPhoneNumberMutationVariables
  >(IMPORT_OWNED_PHONE_NUMBER, {
    refetchQueries: PHONE_NUMBER_REFETCH_QUERIES,
    awaitRefetchQueries: true,
  });

  const { enqueueSnackbar } = useSnackbar();
  const getErrorMessage = useErrorMessageFormatter();
  const isStaff = useCurrentUserIsStaff();
  const subaccountApolloClient = useNonCacheClearingApolloClient();

  const [riskyMoveDetected, setRiskyMoveDetected] = useState(false);

  return (
    <Dialog open={open} maxWidth="md" fullWidth>
      <DialogTitle>Import Phone Number</DialogTitle>
      <Formik
        initialValues={{
          [submitErrorField]: null,
          provider: ProviderName.Bandwidth,
          phoneNumber: "",
          messageClass: "",
          shortCodeConfig: DEFAULT_SHORT_CODE_CONFIG_FORM_VALUES,
          tcrCampaignId: "",
          twilioAccountSid: "",
          allowRiskyMove: false,
          notifyTollFreeCreated: false,
          state: isStaff ? "NEW" : undefined,
        }}
        validationSchema={
          importOwnedPhoneNumberInputSchema(Yup, {
            messageClassEnumValues: Object.values(PhoneNumberMessageClass),
            providerEnumValues: Object.values(ProviderName),
            useCommaSeparatedLists: true,
          }).fields.input
        }
        onSubmit={async (values, formik) => {
          try {
            setRiskyMoveDetected(false);
            const response = await importOwnedPhoneNumber({
              variables: {
                input: {
                  subaccountId,
                  provider: values.provider as ProviderName,
                  phoneNumber: values.phoneNumber,
                  messageClass: values.messageClass as PhoneNumberMessageClass,
                  shortCodeConfig: shortCodeFormValuesToConfig(
                    values.messageClass as PhoneNumberMessageClass,
                    values.shortCodeConfig
                  ),
                  tcrCampaignId: values.tcrCampaignId || null,
                  twilioAccountSid: values.twilioAccountSid || null,
                  allowRiskyMove: values.allowRiskyMove,
                  notifyTollFreeCreated: values.notifyTollFreeCreated,
                  state:
                    isStaff &&
                    values.messageClass === PhoneNumberMessageClass.ShortCode
                      ? values.state
                      : undefined,
                },
              },
            });
            if (response.errors?.length) {
              if (
                response.errors.find(
                  e => e.extensions?.code === "RISKY_PHONE_NUMBER_MOVE"
                )
              ) {
                setRiskyMoveDetected(true);
              }
              formik.setErrors({
                [submitErrorField]: getErrorMessage(response.errors[0]),
              });
            } else {
              enqueueSnackbar("Phone number imported");
              await subaccountApolloClient.refetchQueries({
                include: [GET_SUBACCOUNT_ADMIN_FIELDS],
              });
              formik.resetForm();
              onClose();
            }
          } catch (e) {
            formik.setErrors({
              [submitErrorField]: getErrorMessage(e),
            });
          }
        }}
      >
        {({ isSubmitting, values, isValid, setFieldValue }) => {
          const showOptionToNotify =
            !hasActiveTFNumber &&
            values.messageClass === "TOLL_FREE" &&
            ["FAKE", "BANDWIDTH"].includes(values.provider);
          return (
            <Form>
              <DialogContent sx={{ pt: 0 }}>
                <Typography>
                  This will import a number into your subaccount.
                </Typography>

                <Box sx={{ pt: 1 }}>
                  <FormikErrorBanner />
                </Box>
                <FormikSelect name="provider" label="Provider" fullWidth>
                  {Object.values(ProviderName).map(provider => (
                    <MenuItem key={provider} value={provider}>
                      {provider}
                    </MenuItem>
                  ))}
                </FormikSelect>
                <FormikTextField
                  name="phoneNumber"
                  label="Phone Number"
                  fullWidth
                />
                <FormikSelect
                  name="messageClass"
                  label="Message Class"
                  fullWidth
                >
                  {Object.values(PhoneNumberMessageClass).map(messageClass => (
                    <MenuItem key={messageClass} value={messageClass}>
                      <MessageClassDisplayName messageClass={messageClass} />
                    </MenuItem>
                  ))}
                </FormikSelect>
                {values.provider === ProviderName.Twilio && (
                  <FormikTextField
                    name="twilioAccountSid"
                    label="Twilio Account SID"
                    fullWidth
                  />
                )}
                {isStaff &&
                  values.messageClass === PhoneNumberMessageClass.TenDlc && (
                    <FormikTextField
                      name="tcrCampaignId"
                      label="TCR Campaign ID (optional)"
                      fullWidth
                    />
                  )}
                <PhoneNumberConfig
                  messageClass={
                    (values.messageClass as PhoneNumberMessageClass) || null
                  }
                />
                {isStaff &&
                  values.messageClass === PhoneNumberMessageClass.ShortCode && (
                    <FormikSelect name="state" label="Status" fullWidth>
                      <MenuItem value="NEW">
                        Pending (not usable by clients, can be tested by
                        carriers)
                      </MenuItem>
                      <MenuItem value="ACTIVE">
                        Active (approved by carriers and ready for client use)
                      </MenuItem>
                    </FormikSelect>
                  )}
                {showOptionToNotify && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="notifyTollFreeCreated"
                        value={values.notifyTollFreeCreated}
                        checked={values.notifyTollFreeCreated ?? false}
                        onChange={async e => {
                          await setFieldValue(
                            "notifyTollFreeCreated",
                            !!e.target.checked
                          );
                        }}
                      />
                    }
                    label="Would you like to notify the client (primary contact and subaccount owners) that toll-free is ready?"
                  />
                )}

                {riskyMoveDetected && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="allowRiskyMove"
                        value={values.allowRiskyMove}
                        checked={values.allowRiskyMove}
                        onChange={async e => {
                          await setFieldValue(
                            "allowRiskyMove",
                            !!e.target.checked
                          );
                        }}
                      />
                    }
                    label="Allow risky phone number move?"
                  />
                )}
              </DialogContent>

              <DialogActions>
                <Button
                  disabled={isSubmitting}
                  onClick={onClose}
                  color="secondary"
                >
                  Cancel
                </Button>

                <FormikSubmitButton
                  disabled={isSubmitting || !isValid}
                  color="primary"
                  variant="contained"
                  submitText="Add"
                  loadingText="Adding..."
                  data-testid="add-tenant-button"
                />
              </DialogActions>
            </Form>
          );
        }}
      </Formik>
    </Dialog>
  );
}
